import { TableCellComponentProps, TableActionConfig, RowConfig } from '@/shared/types/table';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  isFlexColumn,
} from '../../utils/cellStyles';
import { InnerButton } from '@/modules/InnerButton';
import { IconType } from '@/shared/types/types';
import { memo } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPen, faTrash, faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { faEye } from '@fortawesome/pro-solid-svg-icons';
// Tipos de acciones predefinidas
type DefaultActionType = 'edit' | 'delete' | 'view' | 'more';

interface DefaultAction {
  type: DefaultActionType;
  icon: IconType | IconProp;
  tooltip: string;
}

// Usar el tipo importado de table.ts
type ActionConfig = TableActionConfig & {
  type?: DefaultActionType;
};

// Tipo para el objeto de acciones por defecto
interface DefaultActionsObject {
  edit?: (row: unknown) => void;
  delete?: (row: unknown) => void;
  view?: (row: unknown) => void;
  more?: (row: unknown) => void;
  editDisabled?: boolean;
  deleteDisabled?: boolean;
  viewDisabled?: boolean;
  moreDisabled?: boolean;
  custom?: ActionConfig[];
  actions?: ActionConfig[];
}

// Configuración de acciones por defecto
const DEFAULT_ACTIONS: Record<DefaultActionType, DefaultAction> = {
  edit: {
    type: 'edit',
    icon: faPen,
    tooltip: 'Editar',
  },
  delete: {
    type: 'delete',
    icon: faTrash,
    tooltip: 'Eliminar',
  },
  view: {
    type: 'view',
    icon: faEye,
    tooltip: 'Ver',
  },
  more: {
    type: 'more',
    icon: faEllipsis,
    tooltip: 'Más opciones',
  },
};

export const ActionsCell = memo(
  ({
    value,
    row,
    column,
    state = 'default',
    className,
    onCellFocus,
    stickyLeft = 0,
    stickyRight,
    isLastLocked = false,
    isActionsColumn = false,
    isRowEditing,
    hasColumnMaxWidthAuto = false,
  }: TableCellComponentProps) => {
    const baseClasses = getTableCellClasses(
      state,
      className,
      column.locked,
      isLastLocked,
      undefined,
      isRowEditing,
      isActionsColumn,
      column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
    );
    const cellProps = getTableCellProps(
      column,
      stickyLeft,
      stickyRight,
      isActionsColumn,
      hasColumnMaxWidthAuto,
    );

    const rowDisabled = (row as RowConfig)?.disabled === true;

    // Si value es un objeto con propiedades de acciones por defecto
    const isDefaultActionsObject = value && typeof value === 'object' && !Array.isArray(value);

    let actions: ActionConfig[] = [];

    if (isDefaultActionsObject) {
      const actionsObj = value as DefaultActionsObject;
      // Generar acciones por defecto si tienen onClick definido
      const defaultActionsToShow: ActionConfig[] = [];

      Object.entries(DEFAULT_ACTIONS).forEach(([key, defaultAction]) => {
        const actionKey = key as DefaultActionType;
        const actionHandler = actionsObj[actionKey];
        if (actionHandler && typeof actionHandler === 'function') {
          const disabledKey = `${actionKey}Disabled` as keyof DefaultActionsObject;
          defaultActionsToShow.push({
            type: actionKey,
            icon: defaultAction.icon as IconType,
            tooltip: defaultAction.tooltip,
            onClick: actionHandler,
            disabled: actionsObj[disabledKey] as boolean | undefined,
          });
        }
      });

      // Agregar acciones personalizadas adicionales
      const customActions = actionsObj.custom || actionsObj.actions || [];
      actions = [...defaultActionsToShow, ...customActions];
    } else if (Array.isArray(value)) {
      // Si es un array, usar el formato legacy
      actions = value;
    } else if (column.actions && Array.isArray(column.actions)) {
      // Usar acciones definidas en la columna
      actions = column.actions as ActionConfig[];
    }

    return (
      <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
        <div className="ui-flex ui-min-w-max ui-flex-shrink-0 ui-items-center ui-gap-2">
          {actions.map((action: ActionConfig, index: number) => {
            const hasLabel = Boolean(action.label?.trim());
            const iconOnRight = hasLabel && (action.iconPosition ?? 'left') === 'right';
            const labelOrIconProps = hasLabel
              ? {
                leftSlot: !iconOnRight ? action.icon : undefined,
                rightSlot: iconOnRight ? action.icon : undefined,
                ...(!iconOnRight && action.colorIcon
                  ? { leftSlotProps: { colorIcon: action.colorIcon } }
                  : {}),
                ...(iconOnRight && action.colorIcon
                  ? { rightSlotProps: { colorIcon: action.colorIcon } }
                  : {}),
                children: action.label,
              }
              : action.colorIcon
                ? { iconProps: { icon: action.icon, color: action.colorIcon } }
                : { iconProps: { icon: action.icon } };
            return (
              <InnerButton
                key={action.type || index}
                theme="solid"
                size="xs"
                pill={hasLabel ? 'sm' : 'xs'}
                color={action.color || 'tertiary'}
                {...labelOrIconProps}
                onClick={() => action.onClick?.(row)}
                disabled={Boolean(action.disabled || state === 'disabled' || rowDisabled)}
                tooltipProps={
                  action.tooltip ? { label: action.tooltip, position: 'top' } : undefined
                }
              />
            );
          })}
        </div>
      </td>
    );
  },
);

ActionsCell.displayName = 'ActionsCell';
